'use client';

import type { ImageLoaderProps } from 'next/image';

import { IMAGINARY_RE, IMGIX_RE, GRAVATAR_RE } from '@/utils/strings/regex';

export function or2ImageLoader(props: ImageLoaderProps) {
  return baseOr2ImageLoader({ ...props, facepad: false });
}

export function or2UserAvatarImageLoader(props: ImageLoaderProps) {
  return baseOr2ImageLoader({ ...props, facepad: true });
}

interface BaseOr2ImageLoaderProps extends ImageLoaderProps {
  facepad: boolean;
}

function baseOr2ImageLoader({
  src,
  width,
  quality,
  facepad,
}: BaseOr2ImageLoaderProps) {
  const dpr =
    global?.window && 'devicePixelRatio' in global.window
      ? global?.window.devicePixelRatio
      : 1;

  let url: URL | null = null;

  try {
    url = new URL(src);
  } catch {
    /* empty */
  }

  if (!url) {
    return src;
  }

  const params = url.searchParams;
  const widthString = width.toString();

  if (width) {
    if (GRAVATAR_RE.test(src)) {
      params.set('s', String(width * dpr));
    }
    // Imaginary
    else if (IMAGINARY_RE.test(src)) {
      const originalHost = url.host;
      const originalPath = url.pathname;
      const s3Url =
        originalHost === 'governorhub-assets.imgix.net'
          ? `${process.env.NEXT_PUBLIC_S3_BASE_URL_ASSETS}${originalPath}`
          : `${process.env.NEXT_PUBLIC_S3_BASE_URL_ORTOO_IMAGES}${originalPath}`;

      params.set('width', params.get('w') || widthString);
      params.set('height', params.get('h') || widthString);
      params.set('quality', (quality || 50).toString());
      params.set('type', 'auto');

      const queryString = params.toString();

      if (facepad) {
        params.set('fit', 'facearea');
        params.set('facepad', '10');
      }

      const operation = facepad ? 'smartcrop' : 'crop';
      const imaginaryUrl = `https://${url.host}/${operation}?${queryString}&url=${s3Url}`;
      return imaginaryUrl;
    }

    // Legacy Imgix
    else if (IMGIX_RE.test(src)) {
      const originalHost = url.host;
      const originalPath = url.pathname;
      let s3Url;

      // GovernorHub assets are stored in a different S3 bucket to ortoo-images
      if (originalHost === 'governorhub-assets.imgix.net') {
        s3Url = `${process.env.NEXT_PUBLIC_S3_BASE_URL_ASSETS}${originalPath}`;
      } else {
        s3Url = `${process.env.NEXT_PUBLIC_S3_BASE_URL_ORTOO_IMAGES}${originalPath}`;
      }

      // Replace the hostname with imaginary.governorhub.com to serve images from imaginary, until we switch URLs in the database
      url.host = 'imaginary.governorhub.com';

      params.set('width', params.get('w') || widthString);
      params.set('height', params.get('h') || widthString);
      params.set('quality', (quality || 50).toString());
      params.set('type', 'auto');

      // Manually construct the query string to avoid URL encoding
      const queryString = params.toString();

      const operation = facepad ? 'smartcrop' : 'crop';
      const imaginaryUrl = `https://${url.host}/${operation}?${queryString}&url=${s3Url}`;

      return imaginaryUrl;
    } else {
      params.set('width', widthString);
    }
  }

  return url.href;
}
